<app-spinner [isLoading]="isLoadingSpinner"></app-spinner>

<div class="payer-shell" *ngIf="!isLoadingSpinner">
  <app-payer-header
    [merchantName]="companyInfo?.CompanyName"
    [merchantLogoUrl]="companyLogo || null"
  ></app-payer-header>

  <main class="payer-main">
    <!-- Lookup form -->
    <ng-container *ngIf="invoiceData === null">
      <div class="page-width-auth u-full-width">
        <div class="card card-elevated">
          <div class="card-body lookup-card-body">
            <h1>Pay your invoice</h1>
            <p>
              Enter the invoice number
              {{ companyInfo?.CompanyName ? companyInfo.CompanyName + ' sent you' : 'from your bill' }}
              and we'll show you the amount due.
            </p>

            <div class="field-group lookup-field-group">
              <div>
                <label for="invoiceNumber" class="form-label">Invoice number</label>
                <input
                  id="invoiceNumber"
                  type="text"
                  inputmode="numeric"
                  placeholder="e.g. 1039"
                  [(ngModel)]="invoiceNumber"
                  [disabled]="isLoading"
                  class="form-control form-control-lg"
                  (keypress)="validateNumber($event)"
                  aria-describedby="invoiceNumberHelp"
                />
                <span id="invoiceNumberHelp" class="form-help">
                  It's printed at the top of the invoice
                  {{ companyInfo?.CompanyName || 'this business' }} sent you.
                </span>
              </div>

              <button
                (click)="getInvoice()"
                [disabled]="isLoading"
                class="btn btn-payer-cta"
              >
                <span *ngIf="!isLoading">Find invoice</span>
                <span *ngIf="isLoading" class="btn-loading">
                  <span class="btn-spinner" aria-hidden="true"></span>
                  Fetching invoice…
                </span>
              </button>
            </div>

            <div
              *ngIf="errorMessage"
              class="alert alert-danger invoice-lookup-message"
              role="alert"
              aria-live="polite"
            >
              <svg class="icon alert-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12" y2="16.01" />
              </svg>
              <span class="alert-body">{{ errorMessage }}</span>
            </div>
          </div>
        </div>

        <p class="text-caption invoice-lookup-wrong-business">
          Wrong business? Check the link in the email
          {{ companyInfo?.CompanyName ? 'from ' + companyInfo.CompanyName : 'you received' }}.
        </p>
      </div>
    </ng-container>

    <!-- Invoice detail -->
    <ng-container *ngIf="invoiceData !== null">
      <div class="page-width-invoice u-full-width">
        <div class="invoice-card">
          <div class="invoice-identity" *ngIf="companyInfo">
            <img
              *ngIf="companyLogo"
              [src]="companyLogo"
              [alt]="companyInfo.CompanyName + ' logo'"
              class="merchant-initials merchant-logo-img"
            />
            <span *ngIf="!companyLogo" class="merchant-initials" aria-hidden="true">{{
              companyInfo.CompanyName ? companyInfo.CompanyName.charAt(0).toUpperCase() : '?'
            }}</span>
            <div>
              <div class="invoice-identity-name">{{ companyInfo.CompanyName }}</div>
              <div class="invoice-identity-email" *ngIf="companyInfo.CompanyAddr?.Line1">
                {{ companyInfo.CompanyAddr?.Line1 }}, {{ companyInfo.CompanyAddr?.City }}
                {{ companyInfo.CompanyAddr?.CountrySubDivisionCode }} {{ companyInfo.CompanyAddr?.PostalCode }}
              </div>
            </div>
          </div>

          <!-- Paid -->
          <ng-container *ngIf="isPaid">
            <div class="invoice-status-row">
              <span class="badge badge-success">
                <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Paid
              </span>
            </div>

            <div class="invoice-paid-confirmation">
              <span class="invoice-paid-icon" aria-hidden="true">
                <svg class="icon icon-lg" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              <div class="invoice-paid-title">Paid in full</div>
              <div class="invoice-paid-body">
                {{ invoiceData.TotalAmt | currency: invoiceData.CurrencyRef?.value }} received for invoice
                {{ invoiceData.DocNumber }}. Recorded in QuickBooks — there's
                nothing left to pay.
              </div>
            </div>

            <div class="invoice-meta-grid">
              <div *ngIf="invoiceData.CustomerRef?.name">
                <div class="invoice-meta-label">Billed to</div>
                <div class="invoice-meta-value">{{ invoiceData.CustomerRef?.name }}</div>
              </div>
              <div *ngIf="invoiceData.TxnDate">
                <div class="invoice-meta-label">Invoice date</div>
                <div class="invoice-meta-value">{{ invoiceData.TxnDate | date }}</div>
              </div>
            </div>

            <div class="invoice-lines">
              <div class="text-overline invoice-lines-heading">Details</div>
              <div class="invoice-line-row" *ngFor="let line of invoiceBreakdown.salesLines">
                <div>
                  <div class="invoice-line-name">{{ line.SalesItemLineDetail?.ItemRef?.name || line.Description }}</div>
                  <div class="invoice-line-qty" *ngIf="line.SalesItemLineDetail?.Qty">
                    {{ line.SalesItemLineDetail?.Qty }} × {{ line.SalesItemLineDetail?.UnitPrice | currency: invoiceData.CurrencyRef?.value }}
                  </div>
                  <div
                    class="invoice-line-desc"
                    *ngIf="line.Description && line.SalesItemLineDetail?.ItemRef?.name && line.Description !== line.SalesItemLineDetail?.ItemRef?.name"
                  >
                    {{ line.Description }}
                  </div>
                </div>
                <div class="invoice-line-amount">{{ line.Amount | currency: invoiceData.CurrencyRef?.value }}</div>
              </div>
            </div>

            <div class="invoice-totals">
              <div class="invoice-totals-row is-subtotal" *ngIf="invoiceBreakdown.subtotal !== null">
                <span>Subtotal</span>
                <span>{{ invoiceBreakdown.subtotal | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-discount" *ngIf="invoiceBreakdown.discountAmount !== null">
                <span>Discount<ng-container *ngIf="invoiceBreakdown.discountPercent !== null"> ({{ invoiceBreakdown.discountPercent }}%)</ng-container></span>
                <span>−{{ invoiceBreakdown.discountAmount | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-shipping" *ngIf="invoiceBreakdown.shippingAmount !== null">
                <span>Shipping</span>
                <span>{{ invoiceBreakdown.shippingAmount | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-tax" *ngIf="invoiceBreakdown.taxAmount !== null">
                <span>Tax<ng-container *ngIf="invoiceBreakdown.taxPercent !== null"> ({{ invoiceBreakdown.taxPercent }}%)</ng-container></span>
                <span>{{ invoiceBreakdown.taxAmount | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-total">
                <span>Total</span>
                <span>{{ invoiceData.TotalAmt | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-paid-amount" *ngIf="invoiceBreakdown.amountPaid > 0">
                <span>Amount paid</span>
                <span>−{{ invoiceBreakdown.amountPaid | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-balance">
                <span>Balance due</span>
                <span>{{ invoiceData.Balance | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
            </div>
          </ng-container>

          <!-- Pending / partially paid -->
          <ng-container *ngIf="!isPaid">
            <div class="invoice-status-row">
              <span class="badge badge-warning">
                <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <polyline points="12 7 12 12 15 14" />
                </svg>
                {{ invoiceData.TotalAmt !== invoiceData.Balance ? 'Partially paid' : 'Pending' }}
              </span>
            </div>

            <div class="invoice-amount-hero">
              <div class="invoice-amount-label">
                {{ invoiceData.TotalAmt !== invoiceData.Balance ? 'Remaining balance' : 'Amount due' }}
              </div>
              <div class="invoice-amount-value">{{ invoiceData.Balance | currency: invoiceData.CurrencyRef?.value }}</div>
              <div class="invoice-amount-meta">
                Invoice {{ invoiceData.DocNumber }}
                <span *ngIf="invoiceData.TotalAmt !== invoiceData.Balance">
                  · {{ invoiceBreakdown.amountPaid | currency: invoiceData.CurrencyRef?.value }} already paid</span
                >
                <span *ngIf="invoiceData.TotalAmt === invoiceData.Balance && invoiceData.DueDate">
                  · due {{ invoiceData.DueDate | date }}</span
                >
              </div>
            </div>

            <div class="invoice-meta-grid">
              <div *ngIf="invoiceData.CustomerRef?.name">
                <div class="invoice-meta-label">Billed to</div>
                <div class="invoice-meta-value">{{ invoiceData.CustomerRef?.name }}</div>
              </div>
              <div *ngIf="invoiceData.TxnDate">
                <div class="invoice-meta-label">Invoice date</div>
                <div class="invoice-meta-value">{{ invoiceData.TxnDate | date }}</div>
              </div>
              <div *ngIf="invoiceData.DueDate">
                <div class="invoice-meta-label">Due date</div>
                <div class="invoice-meta-value">{{ invoiceData.DueDate | date }}</div>
              </div>
            </div>

            <div class="invoice-lines">
              <div class="text-overline invoice-lines-heading">Details</div>
              <div class="invoice-line-row" *ngFor="let line of invoiceBreakdown.salesLines">
                <div>
                  <div class="invoice-line-name">{{ line.SalesItemLineDetail?.ItemRef?.name || line.Description }}</div>
                  <div class="invoice-line-qty" *ngIf="line.SalesItemLineDetail?.Qty">
                    {{ line.SalesItemLineDetail?.Qty }} × {{ line.SalesItemLineDetail?.UnitPrice | currency: invoiceData.CurrencyRef?.value }}
                  </div>
                  <div
                    class="invoice-line-desc"
                    *ngIf="line.Description && line.SalesItemLineDetail?.ItemRef?.name && line.Description !== line.SalesItemLineDetail?.ItemRef?.name"
                  >
                    {{ line.Description }}
                  </div>
                </div>
                <div class="invoice-line-amount">{{ line.Amount | currency: invoiceData.CurrencyRef?.value }}</div>
              </div>
            </div>

            <div class="invoice-totals">
              <div class="invoice-totals-row is-subtotal" *ngIf="invoiceBreakdown.subtotal !== null">
                <span>Subtotal</span>
                <span>{{ invoiceBreakdown.subtotal | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-discount" *ngIf="invoiceBreakdown.discountAmount !== null">
                <span>Discount<ng-container *ngIf="invoiceBreakdown.discountPercent !== null"> ({{ invoiceBreakdown.discountPercent }}%)</ng-container></span>
                <span>−{{ invoiceBreakdown.discountAmount | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-shipping" *ngIf="invoiceBreakdown.shippingAmount !== null">
                <span>Shipping</span>
                <span>{{ invoiceBreakdown.shippingAmount | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-tax" *ngIf="invoiceBreakdown.taxAmount !== null">
                <span>Tax<ng-container *ngIf="invoiceBreakdown.taxPercent !== null"> ({{ invoiceBreakdown.taxPercent }}%)</ng-container></span>
                <span>{{ invoiceBreakdown.taxAmount | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-total">
                <span>Total</span>
                <span>{{ invoiceData.TotalAmt | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-paid-amount" *ngIf="invoiceBreakdown.amountPaid > 0">
                <span>Amount paid</span>
                <span>−{{ invoiceBreakdown.amountPaid | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
              <div class="invoice-totals-row is-balance">
                <span>Balance due</span>
                <span>{{ invoiceData.Balance | currency: invoiceData.CurrencyRef?.value }}</span>
              </div>
            </div>

            <div class="payer-sticky-cta">
              <button (click)="goToPaymentLink()" [disabled]="isLoading" class="btn btn-payer-cta">
                {{ invoiceData.TotalAmt !== invoiceData.Balance ? 'Pay remaining ' : 'Pay this invoice · ' }}{{ invoiceData.Balance | currency: invoiceData.CurrencyRef?.value }}
              </button>
              <p class="text-caption invoice-cta-note">
                You'll be taken to our payment provider's secure checkout. Card
                details are never handled directly by Paierly.
              </p>
            </div>
          </ng-container>

          <button (click)="searchAnotherInvoice()" class="btn btn-ghost invoice-search-another">
            Look up another invoice
          </button>
        </div>
      </div>
    </ng-container>
  </main>

  <div class="payer-secure-badge-row" *ngIf="companyInfo">
    <span class="payer-secure-badge">
      <svg class="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      Secure payment
    </span>
  </div>

  <footer class="payer-footer">
    <span class="invoice-trust-signature"
      >Secure payments powered by <strong>Paierly</strong></span
    >
  </footer>
</div>
